/**
 * prefetchQueue.ts
 * طابور إحماء بحدّ تزامن — الاستضافة المشتركة (LiteSpeed) تقطع تدفّقات HTTP/2 عندما
 * يفتح المتصفح عشرات التنزيلات دفعةً واحدة (ERR_HTTP2_PROTOCOL_ERROR + 206 لاستكمال
 * المقطوع). كل الإحماء غير العاجل (إطارات مجاورة، نُسج التلميح) يمرّ من هنا.
 *
 * ── لماذا صار الطابور ذا أولويّتين ──────────────────────────────────────────────
 * كان طابوراً واحداً بترتيب الوصول (FIFO). وبعد إضافة التخزين المسبق لكامل الأيام
 * الخمسة، صار الإقلاع يُدرج **121 إطاراً × عدد الأنواع** (‏242 مهمّة أو أكثر) دفعةً واحدة
 * بعد نصف ثانية من العرض الأول. ثم حين يبدأ المستخدم التشغيل فعلاً، تُدرج نافذة الإحماء
 * القريبة (‏16 إطاراً — وهي الإطارات التي سيصل إليها الشريط خلال ثوانٍ) **خلف** تلك
 * المئتين، فلا تصل إلا بعد أن ينتهي الطابور من تنزيل اليوم الخامس أولاً.
 *
 * هذا يفسّر بدقّة العرَض المُبلَّغ عنه: أوّل ساعة أو ساعتان سلستان (كانتا قد أُحميتا قبل
 * فيضان الطابور)، ثم يتعثّر كل شيء لأن الإطار الذي ينتظره التشغيل الآن يقبع خلف صفّ من
 * ملفّات لن يراها المستخدم إلا بعد دقائق — إن رآها.
 *
 * الحلّ: صفّان. العاجل (نافذة التشغيل القريبة) يُخدَم دائماً أولاً؛ والخلفي (تعبئة كامل
 * الأيام) يملأ ما تبقّى من طاقة الاتصالات. لا يُلغى شيء ولا تُنقص جودة — يتغيّر الترتيب
 * فقط، وهو كل ما كان ناقصاً.
 */

import { prefetchRasterBytes, prefetchRasterImage } from './rasterImageCache';

/**
 * حدّ التزامن.
 *
 * كان 3 (وقبله 6 ثمّ 16): قياس فعلي بأثر أداء حقيقي (Chrome trace) أظهر طلبات راستر
 * خلفية تأخذ 15-25 ثانية للاكتمال — ليس بطء شبكة، بل تزاحم مع الخادم نفسه (استضافة
 * مشتركة بعدد عمّال محدود) حيث تنافس طلبات الإحماء غير العاجلة طلباتِ الإطار الحالي.
 *
 * صار 6 بعد وضع الموقع خلف حافة Cloudflare بقاعدتَي Cache Rules على ‎/rasters/*‎: الأصل
 * لم يعد هو العنق، إذ تخدم الحافة الملفّ المخزّن من ذاكرتها. تحقُّق فعلي (لا تقدير):
 * أول طلب لـ ‎wind_000.png?v=…‎ أعاد ‎cf-cache-status: MISS‎ والثاني ‎HIT‎ مع ‎Age: 1‎،
 * والملفّات تحمل ‎Cache-Control: max-age=31536000, immutable‎ (الترقيم ‎?v=run_epoch‎ هو
 * ما يبطلها عند دورة جديدة — راجع rasterVersion.ts).
 *
 * الرقم قابل للضبط عبر setPrefetchConcurrency — أعِده إلى 3 إن رجع الأصل ليُخدَم مباشرةً
 * (تعطُّل الوكيل، أو نطاق غير مُمرَّر عبر Cloudflare). ويجب أن يتحرّك مع
 * maplibreConfig.MAX_PARALLEL_IMAGE_REQUESTS في NewWeatherMap.tsx (‏8 اليوم)، فمجموعهما
 * — لا أيٌّ منهما وحده — هو ما يراه الخادم.
 */
let maxConcurrent = 6;

/** يرفع/يخفض حدّ التزامن (‏6-8 مناسبة خلف CDN، و3 على الأصل المشترك مباشرةً). */
export function setPrefetchConcurrency(n: number): void {
    maxConcurrent = Math.max(1, Math.min(12, Math.round(n)));
    pump();
}

type Task = () => void;

let active = 0;
/** العاجل: ما سيصل إليه الشريط خلال ثوانٍ. يُخدَم قبل أي شيء في الصفّ الخلفي. */
const urgent: Task[] = [];
/** الخلفي: تعبئة كامل الأيام — مفيدة لكنها لا تُنافس الإطار القادم أبداً. */
const background: Task[] = [];

/** يلغي الإحماء الذي لم يبدأ بعد عند تغيير النموذج أو القفز لوقت بعيد. */
export function clearQueuedPrefetches(): void {
    urgent.length = 0;
    background.length = 0;
}

/**
 * يُفرغ **الصفّ العاجل وحده** — للسحب البعيد على الشريط الزمني.
 *
 * حين يقفز المستخدم من الساعة 20 إلى الساعة 70، يبقى الصفّ العاجل محتشداً بنافذة
 * الموضع القديم (حتى 16 إطاراً × نوعين = 32 مهمّة) لم تعد تُرى ولن تُطلب. وهي تحتلّ
 * فتحات التزامن الستّ كلّها، فالإطار الذي يحدّق فيه المستخدم الآن يقف خلفها — وهذا
 * بالضبط ما وُصف بـ«أحرّك المؤشّر لقدّام فتتوقّف البيانات وتبدأ تحمّل».
 *
 * **لا يُمسّ الصفّ الخلفي**: تعبئة الأيام الخمسة تُدرَج مرّة واحدة لكل نموذج (حارس
 * `fullWarmed`)، فإفراغها يعني ضياعها إلى الأبد لأن لا أحد يُعيد إدراجها.
 */
export function clearUrgentPrefetches(): void {
    urgent.length = 0;
}

/**
 * ── وضع التركيز: كل النطاق للإطار الذي ينتظره المستخدم الآن ──────────────────────
 *
 * قياس فعلي على الحافة (‏cf-cache-status: HIT): ‏TTFB ≈ 0.26-0.4ث و**‏~250 ك.ب/ث لكل
 * اتصال**. إطار الحرارة 220 ك.ب والرياح 345 ك.ب — أي أن الإطار الواحد يحتاج نحو ثانيتين
 * من نطاقٍ **خالص**. وستّ مهمّات إحماء متزامنة تقتسم النطاق نفسه، فتصير الثانيتان ستّاً
 * أو أكثر: هذا حرفياً بطء «الوقت الذي أنتقل إليه».
 *
 * فحين لا يكون الإطار المعروض جاهزاً، نضيّق الطابور إلى فتحتين ونمنع الصفّ الخلفي من
 * البدء أصلاً. لا يُلغى شيء ولا تنقص جودة — تُؤجَّل الجيرة ثانيةً واحدة كي يصل ما
 * يحدّق فيه المستخدم أولاً، ثم يعود الطابور لسعته الكاملة فيسبق العرضَ كما كان.
 */
let focusMode = false;

/** يُفعّلها المُشترك في جاهزية الإطار الحالي (راجع rasterReadiness). */
export function setPrefetchFocus(on: boolean): void {
    if (focusMode === on) return;
    focusMode = on;
    pump();
}

function pump(): void {
    // فتحتان في وضع التركيز: واحدة تكفي نظرياً، لكن الإطار يحتاج نوعين (الطبقة + الرياح)
    // ومنعُ توازيهما يضاعف زمن الانتظار المُدرَك بلا مكسب في النطاق.
    const limit = focusMode ? 2 : maxConcurrent;
    while (active < limit) {
        // الصفّ الخلفي (تعبئة الأيام الخمسة) لا يبدأ إطلاقاً أثناء التركيز — لا قيمة له
        // قبل أن يرى المستخدم اللحظة التي طلبها.
        const task = urgent.shift() ?? (focusMode ? undefined : background.shift());
        if (!task) return;
        active += 1;
        task();
    }
}

/** ينفّذ مهمة غير عاجلة ضمن حدّ التزامن. `priority` يختار الصفّ. */
export function queuePrefetch(run: () => Promise<unknown>, priority: 'urgent' | 'background' = 'urgent'): void {
    const task: Task = () => {
        run().catch(() => { /* الإحماء اختياري — الفشل لا يهم */ }).finally(() => {
            active -= 1;
            pump();
        });
    };
    (priority === 'urgent' ? urgent : background).push(task);
    pump();
}

/**
 * يُحمّي صورة عبر الطابور.
 *
 * العاجل يفكّ الصورة ويضعها في كاش الصور (جاهزة للرفع الفوري إلى GPU)، أمّا الخلفي
 * فينزّل **البايتات فقط**. الفرق جوهري لا تحسيني: كاش الفكّ سعته 40 صورة والتعبئة
 * الخلفية تمرّ بمئتين وأربعين، فلو فكّتها لطردت كل ما حول موضع المشاهدة — راجع
 * prefetchRasterBytes.
 */
export function queuePrefetchImage(src: string, priority: 'urgent' | 'background' = 'urgent'): void {
    queuePrefetch(() => (priority === 'background' ? prefetchRasterBytes(src) : prefetchRasterImage(src)), priority);
}
